import { useCallback, useEffect, useRef, useState } from 'react'
import { Conversation } from '@elevenlabs/client'
import AgentWave from '../components/AgentWave'
import CapstonLogo from '../components/CapstonLogo'
import CapstonMark from '../components/CapstonMark'
import { api } from '../lib/api'
import { formatTorontoDateTime, formatTorontoWeekdayDateTime } from '../lib/format'
import Markdown from '../lib/markdown'

// The agent's audio tags — `[warm]`, `[laughs]` — arrive inside its lines.
// Dropped before the line is shown or kept, as the server does at ingest
// (services/calls.strip_audio_tags). Participant lines are left as heard.
const AUDIO_TAG = /\[[^[\]]*\]/g
function stripAudioTags(text) {
  return text.replace(AUDIO_TAG, '').replace(/[ \t]{2,}/g, ' ').trim()
}

// The public link page. Mobile-first: whoever opens this is likely on a phone,
// has no account, and can't be asked to retry or contact anyone; every state
// below must read as a complete destination, never a dead end.
//
// All the copy comes from the link's template (call screen, button label,
// thank-you screen), rendered as markdown. This page holds no wording of its own
// beyond the mechanical states — loading, mic blocked, the live call, and the two
// neutral screens — because it cannot know what kind of conversation this is.

// The call's client-side lifecycle. EVERY ended call lands on the same phase, and
// from there the same screen: there is no complete-vs-partial anywhere on this
// side of the app, and nothing here inspects how a call ended.
function useCall(token, { onLinkDead, onEnded }) {
  const [callPhase, setCallPhase] = useState('idle') // idle | starting | live | ended | mic_denied | failed
  // Sticky: a call ended in this page session. `callPhase` can't answer that — it
  // moves on to 'failed' if a retake won't start (429, EL down), and the page
  // would drop back to the call screen underneath someone who has just finished.
  // `data` can't answer it either: it is the pre-call fetch, so on a first call it
  // still says "ready".
  const [hasEnded, setHasEnded] = useState(false)
  const [endedAt, setEndedAt] = useState(null)
  const [failMessage, setFailMessage] = useState(null)
  const [liveStatus, setLiveStatus] = useState('connecting') // connecting | connected | disconnected
  const [mode, setMode] = useState('listening') // listening | speaking
  // The interviewer's most recent line, shown in the in-call card and replaced
  // each time it says something new. Agent turns only — the participant can
  // hear themselves.
  const [lastAgentMessage, setLastAgentMessage] = useState('')
  // Every turn of the current call, for the recap on the thank-you screen. A
  // ref, not state: nothing renders it until the call has ended.
  const turnsRef = useRef([])
  const conversationRef = useRef(null)
  const activeRef = useRef(true)

  useEffect(() => {
    activeRef.current = true
    return () => {
      activeRef.current = false
      // Leaving the page mid-call ends the session; the link stays open, so
      // another call is always available.
      conversationRef.current?.endSession().catch(() => {})
      conversationRef.current = null
    }
  }, [])

  const start = useCallback(async () => {
    setFailMessage(null)
    setCallPhase('starting')
    setLastAgentMessage('')
    turnsRef.current = []

    // Mic permission first — a denial costs nothing (no signed URL minted, no
    // state flip on the server).
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((track) => track.stop())
    } catch {
      if (activeRef.current) setCallPhase('mic_denied')
      return
    }

    let startData
    try {
      startData = await api(`/api/v1/s/${encodeURIComponent(token)}/start`, { method: 'POST' })
    } catch (err) {
      if (!activeRef.current) return
      if (err.status === 404) {
        // The link isn't startable any more — revoked, or expired between the
        // page load and this press. Drop back to idle BEFORE re-checking:
        // otherwise this phase stays 'starting' and paints the live-call view
        // over whatever the re-check finds.
        setCallPhase('idle')
        onLinkDead()
        return
      }
      setFailMessage(
        err.status === 429
          ? 'That was a few calls in a short window — give it a minute, then try again.'
          : "The call couldn't be started. It's not you — please try again in a moment.",
      )
      setCallPhase('failed')
      return
    }

    try {
      setLiveStatus('connecting')
      conversationRef.current = await Conversation.startSession({
        signedUrl: startData.signed_url,
        dynamicVariables: startData.dynamic_variables,
        onStatusChange: ({ status }) => {
          if (activeRef.current) setLiveStatus(status)
        },
        onModeChange: ({ mode: nextMode }) => {
          if (activeRef.current) setMode(nextMode)
        },
        onMessage: ({ source, message }) => {
          if (source === 'ai') message = stripAudioTags(message || '')
          if (!message) return
          turnsRef.current.push({
            speaker: source === 'ai' ? 'agent' : 'participant',
            text: message,
          })
          if (source === 'ai' && activeRef.current) setLastAgentMessage(message)
        },
        onDisconnect: () => {
          conversationRef.current = null
          if (!activeRef.current) return
          // One phase for every ending — the agent's goodbye, a hang-up halfway,
          // a dropped connection. The reason is never shown, and the server hears
          // about the call from the webhook, not from here.
          setHasEnded(true)
          setEndedAt(Date.now())
          setCallPhase('ended')
          onEnded()
        },
        onError: () => {
          // Non-fatal SDK errors surface here; disconnects arrive via
          // onDisconnect. Nothing actionable mid-call.
        },
      })
      if (activeRef.current) setCallPhase('live')
    } catch {
      conversationRef.current = null
      if (!activeRef.current) return
      setFailMessage(
        "We couldn't connect the call. Check your connection and try again — your link is still valid.",
      )
      setCallPhase('failed')
    }
  }, [token, onLinkDead, onEnded])

  const end = useCallback(async () => {
    try {
      await conversationRef.current?.endSession()
    } catch {
      // The happy path routes through onDisconnect; this only fires when
      // endSession itself throws. Same landing either way.
      conversationRef.current = null
      if (!activeRef.current) return
      setHasEnded(true)
      setEndedAt(Date.now())
      setCallPhase('ended')
      onEnded()
    }
  }, [onEnded])

  // The live output spectrum, for the on-screen wave (AgentWave). A getter rather
  // than state on purpose: this is read every frame, and the SDK hands back one
  // pre-allocated array, so nothing here allocates or re-renders.
  const getFrequencyData = useCallback(() => {
    if (!conversationRef.current) return null
    try {
      return conversationRef.current.getOutputByteFrequencyData()
    } catch {
      return null // analyser not up yet
    }
  }, [])

  const getTurns = useCallback(() => turnsRef.current, [])

  return {
    callPhase,
    hasEnded,
    endedAt,
    failMessage,
    liveStatus,
    mode,
    lastAgentMessage,
    getTurns,
    getFrequencyData,
    start,
    end,
  }
}

// The page around every screen: the wordmark on the page itself, above the
// card, so the card holds only the conversation. Same header on every state.
function PageShell({ children }) {
  return (
    <main className="min-h-screen" style={{ background: 'var(--color-surface-alt)' }}>
      <div
        className="w-full px-4 max-w-xl mx-auto flex flex-col"
        style={{
          gap: 'var(--space-m)',
          paddingTop: 'var(--space-l)',
          paddingBottom: 'calc(var(--space-2xl) + env(safe-area-inset-bottom))',
        }}
      >
        <header>
          <CapstonLogo height={40} />
        </header>
        {children}
      </div>
    </main>
  )
}

// The call-to-action block: full-width button + the one quiet line that belongs
// under it. `variant` is the base/subclass seam — ink for the screens whose whole
// purpose is starting a call, secondary for the retake offered beside a call
// already taken, where the default read must stay "you're done".
function StartBlock({ label, caption, onStart, variant = 'primary' }) {
  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
      <button type="button" className={`btn btn--${variant} btn--lg w-full`} onClick={onStart}>
        {label}
      </button>
      {caption && (
        <p
          className="text-body-sm"
          style={{ color: 'var(--color-text-tertiary)', textAlign: 'center' }}
        >
          {caption}
        </p>
      )}
    </div>
  )
}

// One fact between the template's call screen and Start: until when the link
// works. The same row for every template — the copy around it is the
// template's. Who asked, what it is about and why are the agent's to open with,
// not the participant's to read first.
function ContextFacts({ expiresAt }) {
  const rows = [['Link valid until', formatTorontoDateTime(expiresAt)]]
  return (
    <dl className="facts" aria-label="About this conversation">
      {rows.map(([label, value]) => (
        <div key={label} className="facts__row">
          <dt className="facts__label">{label}</dt>
          <dd className="facts__value">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

// The pre-call screen: the template's `call_screen` markdown and the one button
// that starts the call. Also what someone RETURNING to a live link sees — coming
// back means coming back to do it again, so the invitation is the right screen
// and the footnote below carries the one thing that changed. The thank-you screen
// belongs to a call that just ended, not to the link (see PostCallView).
function ReadyView({ data, call }) {
  return (
    <section className="card card--panel">
      <Markdown source={data.call_screen} className="markdown--intro" />

      <ContextFacts expiresAt={data.expires_at} />

      {call.callPhase === 'failed' && (
        <div className="alert alert--error" role="alert" style={{ marginTop: 'var(--space-s)' }}>
          <p className="text-body-sm">{call.failMessage}</p>
        </div>
      )}

      <div style={{ marginTop: 'var(--space-m)' }}>
        <StartBlock label={data.start_label} onStart={call.start} />
      </div>

      <p
        className="text-body-md"
        style={{
          color: 'var(--color-text-tertiary)',
          textAlign: 'center',
          marginTop: 'var(--space-s)',
        }}
      >
        Please allow microphone access in your browser.
      </p>

      {/* Only for someone who has been here before. Quieter than the line above
          it: it explains why this screen looks familiar, and it is never an
          instruction. Absent on a first call, and absent in the window between
          hanging up and the webhook landing — the server has no end time to give
          yet, and a made-up one under "you last completed" would be worse than
          silence. */}
      {data.call_ended_at && (
        <p
          className="text-body-sm"
          style={{
            color: 'var(--color-text-tertiary)',
            textAlign: 'center',
            marginTop: 'var(--space-xs)',
          }}
        >
          You last completed this call on {formatTorontoWeekdayDateTime(data.call_ended_at)}.
        </p>
      )}
    </section>
  )
}

// Mic permission was denied. The fix lives in browser UI outside the page, so
// this walks through it — recoverable, nothing lost.
function MicBlockedView({ call }) {
  return (
    <section className="card card--panel">
      <h1 className="text-display">
        Your microphone is blocked
      </h1>
      <p
        className="text-body-md"
        style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-s)' }}
      >
        Your browser is set to keep this page from using the microphone, and the page
        can&rsquo;t change that itself. It takes about ten seconds to allow it:
      </p>
      <ol className="steps text-body-md" style={{ marginTop: 'var(--space-m)' }}>
        <li>
          Tap the icon next to the web address — usually a padlock, <strong>AA</strong>, or a
          settings symbol.
        </li>
        <li>
          Find <strong>Microphone</strong> and set it to <strong>Allow</strong>.
        </li>
        <li>Come back here and try again.</li>
      </ol>
      <p
        className="text-body-md"
        style={{
          color: 'var(--color-text-tertiary)',
          marginTop: 'var(--space-xs)',
          marginBottom: 'var(--space-m)',
        }}
      >
        Nothing is lost — your link still works.
      </p>
      <StartBlock label="Try again" onStart={call.start} />
    </section>
  )
}

// The hang-up glyph on End call: a handset with a stroke through it.
function HangUpIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M10.7 13.3a13 13 0 0 1-2-2l1.4-1.8a1 1 0 0 0 .1-1L9.2 5.2a1 1 0 0 0-1-.7H5a2 2 0 0 0-2 2 16 16 0 0 0 4.6 10.2" />
      <path d="M14.4 17.1a16 16 0 0 0 3.1 1.8 1 1 0 0 0 1-.3l1.5-1.5a1 1 0 0 1 1-.3l3 1a1 1 0 0 1 .7 1V21a2 2 0 0 1-2 2 16 16 0 0 1-7.6-2.3" />
      <path d="M22 2 2 22" />
    </svg>
  )
}

// The live call, as one white card: an empty head strip, then who is talking —
// the agent's initial in an ink circle, its name, Speaking / Listening, the
// compact wave — and its latest line in the serif under the name as a label;
// End call alone in the foot. No timer: nothing here should make the
// participant watch the clock. `agentName` is the template's; without one the
// avatar carries the mark and the line stands unlabelled.
//
// Leaving early is theirs to decide, so End call asks before acting and then
// says the one thing that is both true and useful: the link stays open.
function LiveCallView({ call, agentName }) {
  const [confirmingEnd, setConfirmingEnd] = useState(false)
  const connecting = call.callPhase === 'starting' || call.liveStatus !== 'connected'
  const waveState = connecting ? 'connecting' : call.mode === 'speaking' ? 'speaking' : 'listening'
  const stateLabel = connecting ? 'Connecting…' : call.mode === 'speaking' ? 'Speaking' : 'Listening'
  const name = (agentName || '').trim()

  return (
    <>
      <section className="card card--panel call-card" aria-label="Your call">
        <div className="call-card__head" aria-hidden="true" />

        <div className="call-card__body">
          <div className="call-card__who">
            <span className="call-avatar" aria-hidden="true">
              {name ? name[0].toUpperCase() : <CapstonMark size={22} color="currentColor" />}
            </span>
            <div className="call-card__name">
              {name && <p className="text-body-md call-card__agent">{name}</p>}
              <p className="text-body-sm call-card__state" role="status">
                {stateLabel}
              </p>
            </div>
            <AgentWave state={waveState} getFrequencyData={call.getFrequencyData} compact />
          </div>

          {call.lastAgentMessage && (
            <div className="call-card__quote" aria-live="polite">
              {name && <p className="overline">{name}</p>}
              <p className="call-card__line">{call.lastAgentMessage}</p>
            </div>
          )}
        </div>

        <div className="call-card__foot">
          {confirmingEnd ? (
            <div className="alert alert--warning" role="alert">
              <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
                <p className="text-body-sm">
                  End the call now? Your link stays open, so you can go through it again from
                  the start whenever you like.
                </p>
                <div className="flex flex-wrap" style={{ gap: 'var(--space-xs)' }}>
                  <button type="button" className="btn btn--danger btn--sm" onClick={call.end}>
                    Yes, end the call
                  </button>
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => setConfirmingEnd(false)}
                  >
                    Keep going
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn--danger btn--lg w-full"
              onClick={() => setConfirmingEnd(true)}
            >
              <HangUpIcon />
              End call
            </button>
          )}
        </div>
      </section>
      <p
        className="text-body-sm"
        style={{ color: 'var(--color-text-tertiary)', textAlign: 'center' }}
      >
        If the call drops, reopen your link — you can start again.
      </p>
    </>
  )
}

// The retake offer, below the thank-you and in secondary weight: the default read
// has to stay "you're done, we have it". Rendered only when the server says the
// link is startable, so an expired link keeps its thank-you without a dead
// button.
//
// It also carries `failMessage`: a start failure after a call renders this card,
// never ReadyView, so this is the only place that message could be seen.
function RetakeBlock({ call, canStart }) {
  if (!canStart) return null
  return (
    <div
      style={{
        marginTop: 'var(--space-m)',
        paddingTop: 'var(--space-m)',
        borderTop: '1px solid var(--color-border-primary)',
      }}
    >
      {call.failMessage && (
        <div className="alert alert--error" role="alert" style={{ marginBottom: 'var(--space-s)' }}>
          <p className="text-body-sm">{call.failMessage}</p>
        </div>
      )}
      <StartBlock label="Start another call" onStart={call.start} variant="secondary" />
    </div>
  )
}

// The participant's recap of the call that ended in THIS tab: the turns the SDK
// handed the page, summarized server-side into at most twelve points. Shown
// once and stored nowhere, so a reload shows the thank-you without it. A
// failure renders nothing — there is nothing the participant could do about it,
// and the thank-you must still read as "you're done".
function CallSummary({ token, call }) {
  const [points, setPoints] = useState(null) // null = pending or failed
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!call.hasEnded) return undefined
    const turns = call.getTurns()
    if (turns.length === 0) return undefined
    let cancelled = false
    setLoading(true)
    api(`/api/v1/s/${encodeURIComponent(token)}/summary`, { method: 'POST', body: { turns } })
      .then((result) => {
        if (!cancelled && result?.points?.length) setPoints(result.points)
      })
      .catch(() => {
        // Nothing to show; the thank-you stands on its own.
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, call.hasEnded, call.endedAt, call.getTurns])

  if (!loading && !points) return null
  return (
    <div className="recap" aria-live="polite" style={{ marginTop: 'var(--space-m)' }}>
      <p className="overline">What you covered</p>
      {loading ? (
        <p className="text-body-sm">
          Putting together a short recap…
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: '1.25em' }}>
          {points.map((point) => (
            <li key={point} className="text-body-md">
              {point}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// The ONE screen every ended call lands on — and only an ended call: this is the
// close of a call, not the state of a link. Coming back later to a link that
// still works lands on ReadyView instead, with the footnote naming the last call.
// The exception is a link that has had a call and can no longer start one, where
// there is nothing to invite anyone to; that reload gets this screen rather than
// the neutral page, which is the "outlives the link" rule. Nothing here reads how
// the call went, because nothing knows.
//
// `{CALL_ENDED_AT}` is the single placeholder the server leaves for us (see
// LinkStateOut). A call that ended in this tab supplies its own end time from the
// client's clock, which is the only accurate source before the webhook lands;
// otherwise the server's `call_ended_at` — the latest call's end, so a retake
// moves it. Rendered to the minute, not as a bare date: two calls on one day have
// to be distinguishable, or the timestamp looks stuck.
function PostCallView({ token, data, call }) {
  const endedAt = call.endedAt ?? data.call_ended_at
  const thanks = data.thanks_screen.replaceAll(
    '{CALL_ENDED_AT}',
    endedAt ? formatTorontoDateTime(endedAt) : 'just now',
  )
  return (
    <section className="card card--panel">
      <Markdown source={thanks} className="markdown--intro" />
      <CallSummary token={token} call={call} />
      <RetakeBlock call={call} canStart={data.can_start} />
    </section>
  )
}

// A draft link opened before its invitation email went out — in practice someone
// previewing the link pre-send. Unlike the neutral page this is truthful: the link
// will start working once the email is sent.
function NotYetActiveView() {
  return (
    <section
      className="card card--panel"
      style={{ paddingTop: 'var(--space-2xl)', paddingBottom: 'var(--space-2xl)' }}
    >
      <h1 className="text-display">
        This link isn&rsquo;t active yet
      </h1>
      <p
        className="text-body-md"
        style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-s)' }}
      >
        Links open once their invitation email has been sent, and this one hasn&rsquo;t gone out
        yet. Keep an eye on your inbox — the email will confirm when it&rsquo;s ready.
      </p>
    </section>
  )
}

// Deliberately spare: expired, revoked, and never-existed all render this same
// screen — no cause, no status. The one suggestion is safe for all three and
// leaks nothing about whether a token ever existed.
function NeutralView() {
  return (
    <section
      className="card card--panel"
      style={{ paddingTop: 'var(--space-2xl)', paddingBottom: 'var(--space-2xl)' }}
    >
      <h1 className="text-display">
        This link isn&rsquo;t active
      </h1>
      <p
        className="text-body-md"
        style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-s)' }}
      >
        If you&rsquo;re expecting an invitation, the most recent email you received has the current
        link.
      </p>
    </section>
  )
}

export default function SessionPage({ token }) {
  const [phase, setPhase] = useState('loading') // loading | view | invalid | error
  const [data, setData] = useState(null)

  const load = useCallback(async () => {
    setPhase('loading')
    try {
      const result = await api(`/api/v1/s/${encodeURIComponent(token)}`)
      setData(result)
      setPhase('view')
    } catch (err) {
      setPhase(err.status === 404 ? 'invalid' : 'error')
    }
  }, [token])

  // A quiet refresh after a call ends: the same fetch without the loading screen,
  // so the thank-you screen stays put while `can_start` and the template copy are
  // re-read. Failures are ignored on purpose — the screen already showing is the
  // right one, and the client's own end time is what dates it.
  const refresh = useCallback(async () => {
    try {
      setData(await api(`/api/v1/s/${encodeURIComponent(token)}`))
    } catch {
      // Keep what we have.
    }
  }, [token])

  const call = useCall(token, { onLinkDead: load, onEnded: refresh })

  useEffect(() => {
    load()
  }, [load])

  if (phase === 'loading') {
    return (
      <PageShell>
        <p className="text-body-md" role="status" style={{ color: 'var(--color-text-secondary)' }}>
          Checking your link…
        </p>
      </PageShell>
    )
  }

  if (phase === 'invalid') {
    return (
      <PageShell>
        <NeutralView />
      </PageShell>
    )
  }

  if (phase === 'error') {
    return (
      <PageShell>
        <div className="alert alert--error" role="alert">
          <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
            <p className="text-body-sm">
              We couldn&rsquo;t check your link — it may be a connection hiccup on our side or
              yours.
            </p>
            <button type="button" className="btn btn--secondary btn--sm self-start" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      </PageShell>
    )
  }

  if (data.state === 'not_yet_active') {
    return (
      <PageShell>
        <NotYetActiveView />
      </PageShell>
    )
  }

  // Order matters below: what is happening IN this page outranks what the server
  // said when the page loaded. Otherwise a retake started from the post-call
  // screen would render that same screen over the running call, and a mic denial
  // on a retake would vanish behind it — `data` is the pre-call fetch and stays
  // stale for the whole call.
  if (call.callPhase === 'starting' || call.callPhase === 'live') {
    return (
      <PageShell>
        <LiveCallView call={call} agentName={data.agent_name} />
      </PageShell>
    )
  }

  if (call.callPhase === 'mic_denied') {
    return (
      <PageShell>
        <MicBlockedView call={call} />
      </PageShell>
    )
  }

  // The thank-you belongs to a call that ended HERE — `hasEnded` is sticky, so it
  // also catches a retake that couldn't start (callPhase 'failed'), which is why
  // RetakeBlock renders `failMessage` too. The one screen it isn't tied to a fresh
  // call is a link past a call that can't start another: ReadyView would offer a
  // button that isn't there, so the thank-you stands in as the closing screen.
  // Every other return visit — link still live — gets ReadyView and its footnote.
  if (call.hasEnded || (data.state === 'post_call' && !data.can_start)) {
    return (
      <PageShell>
        <PostCallView token={token} data={data} call={call} />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <ReadyView data={data} call={call} />
    </PageShell>
  )
}
